import { CheckBoxWithLabelWidget } from '@tableau/widgets';
import { FormikProps, withFormik } from 'formik';
import * as Markdownit from 'markdown-it';
import * as React from 'react';

import { useSettingsCoordinator } from '~src/settings/components/settings-coordinator/SettingsCoordinatorHook';
import { ServerWelcomeChannelInfo } from '~src/ts/ServerApi/requests/GetServerWelcomeChannelInfoRequest';
import { IUpdateServerWelcomeChannelInfoResult } from '~src/ts/ServerApi/requests/UpdateServerWelcomeChannelInfoRequest';

import { Messages } from '../../../codegen/Localize';
import { SettingsWelcomeChannelStyles } from '../styles/SettingsWelcomeChannelStyles';
import { htmlToMarkdown } from '../utils/MarkdownHtml';
import { FooterEditor } from './FooterEditor';
import { WelcomeChannelNavigationDialog } from './WelcomeChannelNavigationDialog';

export interface CustomizationSettingsValues {
  defaultBannerCheckbox: boolean;
  serverFooterCheckbox: boolean;
  serverFooter: {
    html: string;
    isValid: boolean;  // Validation comes from the subComponent
  };
}

export interface CustomizationSettingsOwnProps {
  sendUpdateRequest: (defaultBannerEnabled: boolean, serverFooter: string) => Promise<IUpdateServerWelcomeChannelInfoResult>;
  initialServerWelcomeChannelInfo: ServerWelcomeChannelInfo;
}

export type InnerCustomizationSettingsProps = CustomizationSettingsOwnProps & FormikProps<CustomizationSettingsValues> & CustomizationSettingsValues;

interface CustomizationSettingsState {
  isBusy: boolean;
  showNavigationDialog: boolean;
  navigationCallBack: () => void;
}

/**
 * Nit: Eventually this should be refactored to be a WelcomeChannelCustomizationForm component.
 * When there are more forms on the customization page, that refactor should happen.
 */
export class InnerCustomizationSettings extends React.PureComponent<InnerCustomizationSettingsProps, CustomizationSettingsState> {

  constructor(props: InnerCustomizationSettingsProps) {
    super(props);

    this.state = {
      isBusy: false,
      navigationCallBack: null,
      showNavigationDialog: false,
    };
  }

  private handleChangeEventAdapter = (value: any, fieldName: keyof CustomizationSettingsValues) => {
    const event = {
      target: {
        name: fieldName,
        value
      }
    };
    // Call the Formik handleChange
    this.props.handleChange(event);
  }

  render() {
    return(
      <section>
        <div className="tb-settings-group-name">{Messages.settings_welcomeChannel_title()}</div>
        <div className="tb-indent">
          <p style={SettingsWelcomeChannelStyles.settingsDescription}>{Messages.settings_welcomeChannel_description()}</p>

          <CheckBoxWithLabelWidget
            checked={this.props.values.defaultBannerCheckbox}
            handleChange={(isBoxChecked: boolean) => this.handleChangeEventAdapter(isBoxChecked, 'defaultBannerCheckbox')}
            testId="defaultBanner"
            label={Messages.settings_welcomeChannel_defaultBannerCheckboxLabel()} />

          <CheckBoxWithLabelWidget
            checked={this.props.values.serverFooterCheckbox}
            handleChange={(isBoxChecked: boolean) => this.handleChangeEventAdapter(isBoxChecked, 'serverFooterCheckbox')}
            testId="customServerFooter"
            label={Messages.settings_welcomeChannel_customServerFooterCheckboxLabel()} />

          {this.props.values.serverFooterCheckbox && <FooterEditor
            formName={'ServerFooter-temp'}
            value={this.props.values.serverFooter.html}
            onChange={(html: string, isValid: boolean) => this.handleChangeEventAdapter({
              html,
              isValid
            }, 'serverFooter')}
            showDefaultWelcomeChannel={this.props.values.defaultBannerCheckbox}
          />}
          {this.state.showNavigationDialog && <WelcomeChannelNavigationDialog
            handleClose={this.handleHideNavigationDialog}
            handleNavigation={this.state.navigationCallBack}
            handleSave={this.props.submitForm}
          />}
        </div>
      </section>
    );
  }

  private handleHideNavigationDialog = () => {
    this.setState({
      showNavigationDialog: false
    });
  }
}

const CustomizationSettingCoordinatorWrapper = (props: InnerCustomizationSettingsProps) => {
  useSettingsCoordinator('WelcomeCustomizationServerSettings');
  return (
    <InnerCustomizationSettings {...props} />
  );
};

export const CustomizationSettings = withFormik<CustomizationSettingsOwnProps, CustomizationSettingsValues>({
  enableReinitialize: true,   // Reset the form when initial props change
  mapPropsToValues: props => {
    const initInfo = props.initialServerWelcomeChannelInfo;
    return {
      defaultBannerCheckbox: !!(initInfo && initInfo.welcomeChannelEnabledForServer),
      serverFooterCheckbox: !!(initInfo && initInfo.welcomeChannelServerFooter),
      serverFooter: {
        html: initInfo ? convertMarkdownToHtml(initInfo.welcomeChannelServerFooter) : '',
        isValid: true // Assumes that previously saved value is valid
      }
    };
  },
  validate: (values, _props) => {
    const errors: any = {};
    if (!values.serverFooter.isValid) {
      errors.serverFooter = 'server not valid';
    }
    return errors;
  },
  handleSubmit: (values, actions) => {
    const serverFooterMarkdown = values.serverFooterCheckbox ? htmlToMarkdown(values.serverFooter.html) : '';
    return actions.props.sendUpdateRequest(values.defaultBannerCheckbox, serverFooterMarkdown).then(() => {
      // Reset the form after submission happens
      actions.setSubmitting(false);
      actions.resetForm({values});
    });
  }
})(CustomizationSettingCoordinatorWrapper);

function convertMarkdownToHtml(markdown: string): string {
  return new Markdownit().render(markdown);
}
